import { Divider } from "antd";
import memoize from "memoize-one";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import { BTText } from "@btnet/core-components";
import {
    getChatMessagesUserInteractionsByDiscussionIdGetV2QueryKey,
    useChatMessagesUserInteractionsByDiscussionIdPutV2,
} from "@btnet/services-btnet/handlers/chat-messages/chat-messages";
import type { UserInteractionsContract } from "@btnet/services-btnet/handlers/models/userInteractionsContract";
import { cn } from "@btnet/utilities-core";

import { BdsIconExclamationCircle } from "@buildertrend/components";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { queryClient } from "helpers/queryClient";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLoginTypes, type DiscussionsLinkedTypes } from "types/enum";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { notifyEntityChanged } from "utilities/entity/notifyEntityChanged";
import { reportError } from "utilities/errorHelpers";
import { useActionBeingPerformed } from "utilities/form/form";
import { PortalType } from "utilities/portal/portal";
import { type IWebviewRouteParams } from "utilities/routes";
import { scrollWithinRef } from "utilities/scroll/scroll";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { LegacyBTComment } from "commonComponents/btWrappers/BTComment/LegacyBTComment";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import {
    currentSchemaVersion,
    EditorContent,
} from "commonComponents/btWrappers/editor/editor.types";
import { AddNewCommentForm } from "commonComponents/entity/comment/AddNewCommentForm";
import {
    CommentHandler,
    type ICommentHandler,
} from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.handler";
import {
    type CommentFormActions,
    CommentPermissions,
    type DiscussionCommentEntity,
    type ICommentFormValues,
} from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.types";
import { CommentPresentational } from "commonComponents/entity/comment/CommentPresentational";
import {
    type DiscussionsEntity,
    type ParticipantsResponse,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { PortalInfoContext } from "commonComponents/utilities/InlineOwnerPreview/PortalInfoContext";

import "./CommentContainer.less";

// Note: If changing these, also update the CSS classes
const commentCollapseHeight = 500;
const commentPreviewHeight = 300;

interface ICommentContainerProps {
    discussions: DiscussionsEntity | null;
    handler?: ICommentHandler;
    linkedId: number;
    participants?: ParticipantsResponse;
    linkedType?: DiscussionsLinkedTypes;
    closeOnSend?: boolean;
    isExternal?: boolean;
    reloadData: () => Promise<void>;
    updateParentDiscussionState: (newDiscussions: DiscussionsEntity) => void;
    onClose?: () => void;
    webviewConfig?: IWebviewRouteParams;
    useLegacyStyling?: boolean;
    isReadOnly?: boolean;
    /**
     * @default legacy
     */
    systemMessage?: React.ReactNode;
    emptyState?: React.ReactNode;
    showParticipants?: boolean;
    activeTab?: BTLoginTypes;
    scrollToDiscussionId?: number;
    onBeforeAddComment?: (currentLinkedId: number) => Promise<number | null>;
}

export function getDefaultJSONComment(addUUid: boolean) {
    return new EditorContent(
        {
            schemaVersion: currentSchemaVersion,
            content: null,
            replyContent: undefined,
        },
        addUUid,
    );
}

const defaultHandler = new CommentHandler();
export function CommentContainer({
    discussions,
    handler = defaultHandler,
    linkedId,
    participants,
    linkedType,
    closeOnSend = false,
    isExternal,
    reloadData,
    updateParentDiscussionState,
    onClose,
    webviewConfig,
    systemMessage,
    emptyState,
    isReadOnly,
    useLegacyStyling = true,
    activeTab,
    scrollToDiscussionId,
    onBeforeAddComment,
}: ICommentContainerProps) {
    const [moreCommentsLoaded, setMoreCommentsLoaded] = useState<boolean>(true);
    const [selectedCommentId, setSelectedCommentId] = useState<number | undefined>(undefined);

    const [actionBeingPerformed, performAction] = useActionBeingPerformed<CommentFormActions>();
    const portalInfo = useRequiredContext(PortalInfoContext);

    const allCommentsDiv = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (allCommentsDiv.current) {
            if (allCommentsDiv.current.clientHeight < commentCollapseHeight) {
                setMoreCommentsLoaded(true);
            } else if (allCommentsDiv.current.clientHeight > commentPreviewHeight) {
                setMoreCommentsLoaded(false);
            }
        }
    }, []);

    const loadMoreComments = () => {
        setMoreCommentsLoaded(true);
    };

    const [reactionData, setReactionData] = useState<Record<number, UserInteractionsContract[]>>(
        Object.fromEntries(discussions?.comments.comments.map((c) => [c.id, c.reactions]) ?? []),
    );

    const commentReactionPut = useChatMessagesUserInteractionsByDiscussionIdPutV2({
        mutation: {
            onSuccess: (res, { discussionId }) => {
                queryClient.setQueryData(
                    getChatMessagesUserInteractionsByDiscussionIdGetV2QueryKey(discussionId),
                    {
                        res,
                    },
                );
                setReactionData({
                    ...reactionData,
                    [discussionId]: res.data,
                });
            },
            onError: (e) => {
                showAPIErrorMessage(e);
            },
        },
    });

    const handleCommentReaction = useCallback(
        async (commentId: number, reaction: UserInteractionsContract) => {
            await commentReactionPut.mutateAsync({ discussionId: commentId, data: reaction });
        },
        [commentReactionPut],
    );

    const filterSubsAndOwnerNotifications = memoize(
        (formValues: ICommentFormValues, notificationUsers: BTSelectItem<undefined>[]) => {
            let result = formValues.notificationUsers;
            if (formValues.showOwner && formValues.showSubs) {
                return formValues;
            }
            const ownerIds = notificationUsers
                .filter((f) => f.title === "Owners" || f.title === "Clients")[0]
                ?.children?.map((child) => parseInt(child.id));
            const subIds = notificationUsers
                .filter((f) => f.title === "Subs/Vendors")[0]
                ?.children?.map((child) => parseInt(child.id));
            if (!formValues.showOwner && ownerIds) {
                result = result.filter((f) => !ownerIds.includes(f));
            }
            if (!formValues.showSubs && subIds) {
                result = result.filter((f) => !subIds.includes(f));
            }
            return { ...formValues, notificationUsers: result };
        },
    );

    const onAddComment = async (
        linkedId: number,
        newComment: ICommentFormValues,
        action: CommentFormActions,
    ): Promise<boolean> => {
        let success = false;
        await performAction({
            actionBeingPerformed: action,
            callback: async () => {
                try {
                    if (!discussions) {
                        return;
                    }

                    let effectiveLinkedId = linkedId;
                    if (onBeforeAddComment) {
                        const resolvedLinkedId = await onBeforeAddComment(linkedId);
                        if (resolvedLinkedId === null || resolvedLinkedId <= 0) {
                            return;
                        }
                        effectiveLinkedId = resolvedLinkedId;
                    }

                    const newCommentContent = await handler.addComment(effectiveLinkedId, {
                        ...filterSubsAndOwnerNotifications(
                            newComment,
                            discussions.comments.notificationUsers,
                        ),
                        channelId: discussions.channelId ?? null,
                    });
                    const newDiscussions = { ...discussions };
                    const newCommentsList = [
                        newCommentContent.comment,
                        ...discussions.comments.comments,
                    ];
                    newDiscussions.comments.comments = newCommentsList;
                    newDiscussions.channelId = newCommentContent.channelId;
                    updateParentDiscussionState(newDiscussions);
                    notifyEntityChanged("comments", "add", [newCommentContent.comment.id]);
                    success = true;
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
        return success;
    };

    const onDeleteComment = (comment: DiscussionCommentEntity) => {
        btDeleteConfirm({
            entityName: "Comment",
            onOk: async () => {
                try {
                    await handler.deleteComment(comment.id);

                    // Remove deleted comment
                    let newCommentsList: DiscussionCommentEntity[] = [
                        ...discussions!.comments.comments,
                    ];
                    newCommentsList = newCommentsList.filter((c) => c.id !== comment.id);

                    // Reset state to no longer include the deleted comment
                    const newDiscussions = { ...discussions! };
                    newDiscussions.comments.comments = newCommentsList;
                    if (newCommentsList.length === 0) {
                        // We have deleted the last comment. Null out channelId
                        newDiscussions.channelId = null;
                    }
                    notifyEntityChanged("comments", "delete", [comment.id]);
                    updateParentDiscussionState(newDiscussions);
                    await reloadData();
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    const replyToListeners: Record<string, (comment: DiscussionCommentEntity) => void> = {};
    const addReplyToListener = (
        name: string,
        listener: (comment: DiscussionCommentEntity) => void,
    ) => {
        replyToListeners[name] = listener;
    };

    const onReplyToComment = (comment: DiscussionCommentEntity) => {
        Object.keys(replyToListeners).forEach((name) => replyToListeners[name](comment));
    };

    const handleFirstCommentRead = async () => {
        if (!discussions || isExternal) {
            return;
        }
        try {
            if (!discussions.channelId) {
                throw new Error("ChannelId is undefined");
            }
            await handler.readComment(discussions.channelId);
        } catch (error) {
            reportError(error);
        }
    };

    const renderComment = (
        index: number,
        comment: DiscussionCommentEntity,
        isEntireConversationUnread: number | boolean | null,
    ) => {
        const isFirst = index === 0;
        return (
            <Fragment key={comment.id}>
                <LegacyBTComment
                    key={comment.id}
                    comment={comment}
                    onDeleteComment={onDeleteComment}
                    showCommentInfo={discussions!.showCommentInfo}
                    onView={isFirst ? () => handleFirstCommentRead() : undefined}
                    isUnread={comment.isUnread}
                    reactionData={reactionData[comment.id] ?? []}
                    onCommentReaction={handleCommentReaction}
                    isExternal={isExternal}
                    isReadOnly={isReadOnly}
                />
                {comment.id === discussions!.oldestUnreadCommentId &&
                    !isEntireConversationUnread && <Divider>Last Read</Divider>}
            </Fragment>
        );
    };

    const disc = discussions!;
    const addNewCommentPermissions =
        disc.addNewCommentPermissions ??
        new CommentPermissions({
            canDelete: true,
            canShowOwner: true,
            canShowSubs: true,
            defaultShowSubs: true,
            defaultShowOwner: true,
        });
    const lastComment =
        disc.comments.comments.length > 0
            ? disc.comments.comments[disc.comments.comments.length - 1]
            : null;
    const isEntireConversationUnread =
        disc.oldestUnreadCommentId &&
        (!lastComment || lastComment.id === disc.oldestUnreadCommentId);

    const comments: React.ReactNode = disc.comments.comments.map((comment, index) =>
        renderComment(index, comment, isEntireConversationUnread),
    );

    const defaultComment = disc.hasAddCommentPermissions
        ? {
              discussionText: "",
              linkedType: disc.linkedDiscussionEntities.commentLinkedType,
              showOwner: addNewCommentPermissions.defaultShowOwner,
              showSubs: addNewCommentPermissions.defaultShowSubs,
              notificationUsers: disc.comments.notificationUsers,
              mentionableUsers: disc.comments.mentionableUsers,
              channelId: disc.channelId,
          }
        : undefined;

    const commentList = (
        <div
            className={cn({
                "LegacyCommentContainer-CommentLoaded": moreCommentsLoaded,
                "LegacyCommentContainer-CommentFadeout": !moreCommentsLoaded,
            })}
        >
            <div className="flex flex-col LegacyCommentContainer-Comments" ref={allCommentsDiv}>
                {comments}
            </div>
        </div>
    );

    const loadMoreButton = !moreCommentsLoaded && useLegacyStyling && (
        <div className="LegacyCommentContainer-LoadOlderComments">
            <BTButtonDeprecated
                type="link"
                onClick={loadMoreComments}
                data-testid="loadOlderComments"
            >
                <strong>Load Older Comments</strong>
            </BTButtonDeprecated>
        </div>
    );

    const container = (
        <div data-testid={`comments-${linkedId}`}>
            {disc.hasAddCommentPermissions && !disc.restrictSubAccess && !isReadOnly && (
                <AddNewCommentForm
                    comment={{
                        ...defaultComment!,
                        commentJson: getDefaultJSONComment(false),
                    }}
                    notificationUsers={disc.comments.notificationUsers}
                    mentionableUsers={disc.comments.mentionableUsers}
                    linkedId={linkedId}
                    onAddComment={onAddComment}
                    addNewCommentPermissions={addNewCommentPermissions}
                    defaultNotifyUsers={disc.comments.defaultNotifyUsers}
                    reloadData={reloadData}
                    linkedType={linkedType}
                    filterSubsAndOwnerNotifications={filterSubsAndOwnerNotifications}
                    closeOnSend={closeOnSend}
                    onClose={onClose}
                    actionBeingPerformed={actionBeingPerformed}
                    webviewConfig={webviewConfig}
                    jobId={disc.jobId}
                    addReplyToListener={addReplyToListener}
                />
            )}

            {commentList}

            {loadMoreButton}
        </div>
    );

    const containerRef = useRef<HTMLDivElement>(null);

    const scheduleScrollWithinRef = useCallback(
        (target: HTMLDivElement, top: number, smooth: boolean) => {
            if (!target) {
                return;
            }
            requestAnimationFrame(() => {
                scrollWithinRef(target, top, smooth);
            });
        },
        [],
    );

    useEffect(() => {
        if (scrollToDiscussionId && containerRef.current) {
            const targetElement = containerRef.current.querySelector(
                `#comment-${scrollToDiscussionId}`,
            );
            if (targetElement) {
                const offsetHeight = (targetElement as HTMLDivElement).offsetTop;
                scheduleScrollWithinRef(containerRef.current, offsetHeight, true);
            }
        }
    }, [scrollToDiscussionId, scheduleScrollWithinRef]);

    // Render based on legacy styling flag
    if (useLegacyStyling) {
        return container;
    } else {
        const input = disc.hasAddCommentPermissions &&
            !disc.restrictSubAccess &&
            !isReadOnly &&
            defaultComment && (
                <AddNewCommentForm
                    comment={{
                        ...defaultComment,
                        commentJson: getDefaultJSONComment(false),
                    }}
                    notificationUsers={disc.comments.notificationUsers}
                    mentionableUsers={disc.comments.mentionableUsers}
                    linkedId={linkedId}
                    onAddComment={async (linkedId, newComment, action) => {
                        const resp = await onAddComment(linkedId, newComment, action);
                        if (resp && containerRef.current) {
                            scheduleScrollWithinRef(
                                containerRef.current,
                                containerRef.current.scrollHeight,
                                true,
                            );
                        }
                        return resp;
                    }}
                    addNewCommentPermissions={addNewCommentPermissions}
                    defaultNotifyUsers={disc.comments.defaultNotifyUsers}
                    reloadData={reloadData}
                    linkedType={linkedType}
                    filterSubsAndOwnerNotifications={filterSubsAndOwnerNotifications}
                    closeOnSend={closeOnSend}
                    onClose={onClose}
                    actionBeingPerformed={actionBeingPerformed}
                    webviewConfig={webviewConfig}
                    jobId={disc.jobId}
                    addReplyToListener={addReplyToListener}
                    additionalFooterContent={
                        portalInfo.currentPortal === PortalType.OWNER ? undefined : systemMessage
                    }
                    participants={participants}
                />
            );
        return (
            <div
                onMouseOver={(ev) => {
                    const target = ev.target as HTMLElement;
                    if (
                        target.matches(".Comment *") &&
                        !target.matches(".Comment .HoverButton *")
                    ) {
                        const commentElement = target.closest(".Comment")!;
                        const newId = parseInt(commentElement.id.split("-")[1]) ?? undefined;
                        if (newId !== selectedCommentId) {
                            setSelectedCommentId(newId);
                        }
                    } else {
                        setSelectedCommentId(undefined);
                    }
                }}
                className="CommentContainer bds-col ant-modal-content"
                style={{ boxShadow: "none" }}
                data-testid={`comments-container-${linkedId}`}
            >
                <div
                    ref={containerRef}
                    className={cn("flex flex-col", "comment-list", { "empty-list": emptyState })}
                    style={{
                        overflowY: "auto",
                        overflowX: "hidden",
                        flexFlow: !emptyState ? "column-reverse" : "column",
                        height: "100%",
                    }}
                    key={disc.channelId}
                >
                    {disc.comments.comments.map((comment, index) => {
                        const isFirst = index === 0;
                        const divider = comment.id === disc.oldestUnreadCommentId &&
                            !isEntireConversationUnread && (
                                <div className="flex flex-row py-2 gap-8 items-center">
                                    <div className="flex flex-col CommentDivider" />
                                    <BTText
                                        size="normal-sm"
                                        className="CommentDividerText"
                                        text="Last read"
                                    />
                                    <div className="flex flex-col CommentDivider" />
                                </div>
                            );
                        const isMostRecentItem = index === 0;
                        return (
                            <Fragment key={comment.id}>
                                {!isMostRecentItem && divider}
                                <CommentPresentational
                                    comment={comment}
                                    onView={isFirst ? () => handleFirstCommentRead() : undefined}
                                    onDeleteComment={onDeleteComment}
                                    showCommentInfo={disc.showCommentInfo}
                                    onReplyToComment={() => onReplyToComment(comment)}
                                    isCommentSelected={selectedCommentId === comment.id}
                                    onCommentReaction={handleCommentReaction}
                                    reactionData={reactionData[comment.id] ?? []}
                                    isExternal={isExternal}
                                    isReadOnly={isReadOnly}
                                />
                            </Fragment>
                        );
                    })}
                    {emptyState === undefined && systemMessage}
                    {emptyState !== undefined && (
                        <div className="CommentEmptyState">{emptyState}</div>
                    )}
                </div>
                {portalInfo.currentPortal === PortalType.BUILDER &&
                    ((activeTab === BTLoginTypes.SUBS && participants?.subs.length === 0) ||
                        (activeTab === BTLoginTypes.OWNER &&
                            participants?.owners.length === 0)) && (
                        <BTAlert
                            type="info"
                            data-testid="sub-client-warning"
                            className="SubClientWarning"
                            message={
                                activeTab === BTLoginTypes.SUBS
                                    ? "Subs/vendors can't see this item yet"
                                    : "Clients can't see this item yet"
                            }
                            showIcon
                            icon={<BdsIconExclamationCircle className="ExclamationIcon" />}
                        />
                    )}
                {input}
            </div>
        );
    }
}
